/**
 * 📄 CSV SMTP Import
 * Importiert SMTPs im Format: host,port,email,password
 */

import React, { useState } from 'react';
import { Upload, CheckCircle, X } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const CsvSmtpImport = ({ onClose, onSuccess }) => {
  const [csvData, setCsvData] = useState('');
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);

  const parseCsv = (text) => {
    const lines = text.trim().split('\n');
    const smtps = [];
    
    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      
      // Format: host,port,email,password
      const parts = trimmed.split(',').map(p => p.trim());
      
      if (parts.length >= 4) {
        const [host, port, email, password] = parts;
        
        // Encryption basierend auf Port
        let encryption = 'tls';
        if (parseInt(port) === 465) encryption = 'ssl';
        else if (parseInt(port) === 25) encryption = 'none';
        
        smtps.push({
          host,
          port: parseInt(port),
          username: email,
          password,
          from_email: email,
          from_name: email.split('@')[0],
          encryption,
          is_active: 1,
          daily_limit: 100000,  // Standard: 100k
          line: idx + 1
        });
      }
    });
    
    return smtps;
  };

  const handlePreview = () => {
    const parsed = parseCsv(csvData);
    setPreview(parsed);
    if (parsed.length > 0) {
      toast.success(`✅ ${parsed.length} SMTPs erkannt`);
    } else {
      toast.error('Keine gültigen SMTPs gefunden');
    }
  };

  const handleImport = async () => {
    if (preview.length === 0) {
      toast.error('Bitte zuerst Preview erstellen');
      return;
    }

    setLoading(true);
    const toastId = toast.loading('🔍 Teste und importiere SMTPs über Admin-Proxy...');

    try {
      // Neue CSV-Import API - testet alle SMTPs asynchron mit Proxy
      const response = await axios.post('/api/smtp/csv-import', {
        smtps: preview
      }, {
        timeout: 300000  // 5 Minuten Timeout für große Imports
      });

      if (response.data.success) {
        const { successful, failed, total, details } = response.data.data;
        
        // Zeige detaillierte Fehler in Console
        details.filter(d => d.status === 'failed').forEach(d => {
          console.error(`❌ ${d.email}: [${d.errorCode}] ${d.error}`);
        });

        toast.success(
          `✅ ${successful}/${total} SMTPs erfolgreich importiert` +
          `${failed > 0 ? ` • ${failed} fehlgeschlagen` : ''}`,
          { id: toastId, duration: 5000 }
        );
        
        if (onSuccess) onSuccess();
        if (successful > 0 && onClose) onClose();
      } else {
        throw new Error(response.data.error || 'Import fehlgeschlagen');
      }
      
    } catch (error) {
      toast.error('Import fehlgeschlagen: ' + (error.response?.data?.error || error.message), { id: toastId });
      console.error('CSV-Import Fehler:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <Upload className="h-6 w-6 mr-2 text-blue-600" />
              CSV SMTP Import
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* CSV Input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                CSV-Daten (Format: host,port,email,password)
              </label>
              <textarea
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md font-mono text-sm"
                placeholder="smtp.example.com,587,user@example.com,password123&#10;mail.domain.com,465,info@domain.com,secret456&#10;..."
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                📋 Eine Zeile pro SMTP. Port 465 = SSL, Port 587 = TLS, Port 25 = Keine Verschlüsselung
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
                🔒 Import testet alle SMTPs parallel über Admin-Proxy und speichert nur funktionierende Server
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handlePreview}
                disabled={!csvData.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Vorschau erstellen
              </button>
              
              {preview.length > 0 && (
                <button
                  onClick={handleImport}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Importiere...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>{preview.length} SMTPs importieren</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Preview */}
            {preview.length > 0 && (
              <div className="border border-gray-300 dark:border-dark-copper rounded-lg p-4 bg-gray-50 dark:bg-dark-surface">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Vorschau ({preview.length} SMTPs)
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {preview.map((smtp, idx) => (
                    <div key={idx} className="bg-white dark:bg-dark-card p-3 rounded border border-gray-200 dark:border-dark-copper text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {smtp.from_email}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-300">
                            {smtp.host}:{smtp.port} ({smtp.encryption.toUpperCase()})
                          </div>
                        </div>
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvSmtpImport;

